/* FP lazy autoplay video (2026-10-19). Pairs with snippets/video.liquid.
 *
 * Autoplay videos are muted ambient loops. The snippet renders them with no
 * src and their mp4 source in data-fp-sources ("width height url|..."). When
 * one comes within 200px of the viewport we pick the smallest rendition that
 * covers its rendered width at up to 2x density, never above 720p on a phone
 * and always the smallest file on Data Saver / 2g / 3g, then play it.
 * Offscreen it pauses, so a carousel never decodes four videos at once. A
 * video the viewer paused (controls) or that finished (no loop) stays put.
 *
 * Liquid only exposes the top mp4 (1080p). Shopify transcodes a fixed ladder
 * beside it under the same file name with a different rendition token, so the
 * smaller files are derived from that URL. If a derived file is missing, the
 * video falls back to the original URL once.
 *
 * prefers-reduced-motion: the element is removed, and the poster's own play
 * button (Horizon's click-to-play) takes over.
 *
 * Loaded as a module from the snippet: a module URL runs once per page no
 * matter how many videos include it.
 */
const SELECTOR = 'video[data-fp-sources]';
const PHONE = window.matchMedia('(max-width: 749px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const PHONE_MAX_HEIGHT = 720;
const RENDITION_TOKEN = /(?:SD|HD|UHD)-\d+p-[\d.]+Mbps/;
const LADDER = [
  { height: 480, token: 'SD-480p-1.5Mbps' },
  { height: 720, token: 'HD-720p-4.5Mbps' },
];

/**
 * @typedef {{ width: number, height: number, url: string, derived?: boolean }} Rendition
 * @param {HTMLVideoElement} video
 * @returns {Rendition[]}
 */
function renditions(video) {
  /** @type {Rendition[]} */
  const listed = (video.dataset.fpSources || '')
    .split('|')
    .map((entry) => {
      const [width, height, url] = entry.trim().split(' ');
      return { width: Number(width), height: Number(height), url };
    })
    .filter((source) => source.width > 0 && source.height > 0 && source.url);

  const all = [...listed];
  for (const source of listed) {
    if (!RENDITION_TOKEN.test(source.url)) continue;
    for (const step of LADDER) {
      if (step.height >= source.height || all.some((known) => known.height === step.height)) continue;
      all.push({
        width: Math.round((source.width * step.height) / source.height),
        height: step.height,
        url: source.url.replace(RENDITION_TOKEN, step.token),
        derived: true,
      });
    }
  }
  return all.sort((a, b) => a.width - b.width);
}

function slowConnection() {
  const connection = navigator.connection;
  if (!connection) return false;
  return connection.saveData === true || /(^|-)2g$|^3g$/i.test(connection.effectiveType || '');
}

/**
 * @param {HTMLVideoElement} video
 * @param {Rendition[]} list - sorted by width, ascending
 */
function pickRendition(video, list) {
  if (slowConnection()) return list[0];

  const density = Math.min(window.devicePixelRatio || 1, 2);
  const needed = video.getBoundingClientRect().width * density * 0.9;
  let choice = list.find((source) => source.width >= needed) || list[list.length - 1];

  if (PHONE.matches && choice.height > PHONE_MAX_HEIGHT) {
    const capped = list.filter((source) => source.height <= PHONE_MAX_HEIGHT);
    if (capped.length) choice = capped[capped.length - 1];
  }
  return choice;
}

/** @param {HTMLVideoElement} video */
function start(video) {
  const playing = video.play();
  if (playing && typeof playing.catch === 'function') playing.catch(() => {});
}

/**
 * Loads the chosen rendition the first time. If it was derived and turns out
 * to be missing (a format/404 error, not a stalled download), switches to the
 * original file once and plays it only if the video is still on screen.
 * @param {HTMLVideoElement} video
 */
function load(video) {
  const list = renditions(video);
  if (!list.length) return false;
  const choice = pickRendition(video, list);
  const original = list.filter((source) => !source.derived).pop();
  if (choice.derived && original) {
    let fellBack = false;
    video.addEventListener('error', () => {
      const error = video.error;
      if (fellBack || !error || error.code !== error.MEDIA_ERR_SRC_NOT_SUPPORTED) return;
      fellBack = true;
      video.src = original.url;
      if (video.dataset.fpInView === '1') start(video);
    });
  }
  video.muted = true;
  video.src = choice.url;
  return true;
}

/** @param {HTMLVideoElement} video */
function show(video) {
  video.dataset.fpInView = '1';
  if (video.dataset.fpUserPaused === '1') return;
  if (video.ended && !video.loop) return;
  if (!video.getAttribute('src') && !load(video)) return;
  start(video);
}

/** @param {HTMLVideoElement} video */
function hide(video) {
  video.dataset.fpInView = '0';
  if (!video.getAttribute('src') || video.paused) return;
  video.dataset.fpAutoPause = '1';
  video.pause();
}

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const video = /** @type {HTMLVideoElement} */ (entry.target);
      if (entry.isIntersecting) show(video);
      else hide(video);
    }
  },
  { rootMargin: '200px 0px' }
);

/** @param {HTMLVideoElement} video */
function bind(video) {
  /* Our offscreen pause is not the viewer's; any other pause (controls,
     Horizon pausing it for another video) is respected until they play it. */
  video.addEventListener('pause', () => {
    if (video.dataset.fpAutoPause === '1') {
      delete video.dataset.fpAutoPause;
    } else if (!video.ended) {
      video.dataset.fpUserPaused = '1';
    }
  });
  video.addEventListener('play', () => {
    delete video.dataset.fpUserPaused;
  });
  observer.observe(video);
}

/** @param {ParentNode} root */
function scan(root) {
  for (const video of root.querySelectorAll(SELECTOR)) {
    if (!(video instanceof HTMLVideoElement) || video.dataset.fpLazyBound === '1') continue;
    video.dataset.fpLazyBound = '1';
    if (REDUCED_MOTION.matches) {
      video.closest('deferred-media')?.removeAttribute('data-fp-lazy-video');
      video.remove();
      continue;
    }
    bind(video);
  }
}

scan(document);
/* Theme editor re-renders sections in place. */
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scan(event.target);
});
